import React from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { GROUP_PRIORITY_OPTIONS } from './constants';
import { formatIban } from './utils';
import { LegalBlock, SummaryCard } from './shared';
import { formatDate, formatTimeRangeUhr, vokalSpelling } from '@/lib/utils';
import { formatCourseOfferLine } from './courseOfferLabel';
import { formatAvailability } from '@/lib/availability';
import { composePostalAddress } from '@/lib/addressLine';
import { legalDocumentHref } from './legalDocument';

function docLink(config, kind) {
  const href = legalDocumentHref(config, kind);
  const label = { agb: 'AGB', datenschutz: 'Datenschutzerklärung', widerruf: 'Widerrufsbelehrung' }[kind];
  if (!href) return label;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-brand-red underline"
      data-testid={`public-registration-consent-link-${kind}`}
      onClick={(e) => e.stopPropagation()}
    >
      {label}
    </a>
  );
}

function ConsentRow({ checked, onChange, invalid, testId, children }) {
  return (
    <label
      className="flex items-start gap-3 text-sm cursor-pointer"
      data-testid={`${testId}-row`}
      onClick={(e) => {
        if (e.target.closest('a')) e.preventDefault();
      }}
    >
      <Checkbox
        checked={checked}
        onCheckedChange={onChange}
        className={invalid ? 'border-red-500' : ''}
        data-testid={testId}
      />
      <span className={invalid ? 'text-red-600 font-medium' : 'text-slate-700'}>{children}</span>
    </label>
  );
}

export default function StepSummary({ form, config, setField, fc, title, invalid }) {
  const c = form.consents;
  const card = form.muensterland_card || { has_card: false, number: '' };
  const schoolName = config?.tenant_name || 'Musikschule';
  const answeredCustom = fc.custom_fields
    .map((cf) => ({ ...cf, value: form.custom[cf.id] }))
    .filter((cf) => (cf.type === 'checkbox' ? cf.value : cf.value && String(cf.value).trim()));
  return (
    <div className="space-y-5" data-testid="public-registration-step-summary">
      <h2 className="text-lg md:text-lg font-semibold text-slate-900">{title}</h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <SummaryCard title="Schüler:in">
          {form.student.first_name} {form.student.last_name}<br />
          <span className="text-slate-500">
            {form.student.category ? `${vokalSpelling(form.student.category)} · ` : ''}{form.student.instrument}
            {form.teaching_type ? ` · ${form.teaching_type}` : ''}
          </span>
        </SummaryCard>
        {(form.course_offer || form.location_id) && (
          <SummaryCard title="Unterricht">
            {form.course_offer && <div>Kursangebot: {formatCourseOfferLine(form.course_offer)}</div>}
            {form.course_offer?.location_text ? (
              <div className="text-slate-500" data-testid="public-registration-summary-location">
                Ort: {form.course_offer.location_text}
              </div>
            ) : !form.course_offer && form.location_id ? (
              <div className="text-slate-500" data-testid="public-registration-summary-location">
                Ort: {(config?.locations || []).find((l) => l.id === form.location_id)?.name || ''}
              </div>
            ) : null}
          </SummaryCard>
        )}
        {(() => {
          const opts = (config?.subject_teaching_options?.[form.student.instrument] || [])
            .map((o) => (typeof o === 'string' ? { name: o, price_text: '' } : o));
          const ttPrice = opts.find((o) => o.name === form.teaching_type)?.price_text || '';
          const costText = form.course_offer?.cost_text || ttPrice;
          if (!costText) return null;
          return (
            <SummaryCard title="Kosten">
              <div data-testid="public-registration-summary-cost">
                {form.course_offer ? formatCourseOfferLine(form.course_offer) : form.teaching_type}
                <span className="text-slate-500"> — {costText}</span>
              </div>
              <div className="text-xs text-slate-400 mt-1">Alle Angaben ohne Gewähr; es gilt die aktuelle Tarifordnung.</div>
            </SummaryCard>
          );
        })()}
        {form.student.lesson_type === 'Gruppenunterricht' && form.group.priority && (
          <SummaryCard title="Gruppenunterricht">
            <span className="text-slate-500">
              {(GROUP_PRIORITY_OPTIONS.find((o) => o.value === form.group.priority) || {}).label}
            </span>
            {form.group.priority === 'add_students' && form.group.additional_students.length > 0 && (
              <div className="mt-1">
                {form.group.additional_students.map((s, i) => (
                  <div key={i}>{s.first_name} {s.last_name}{s.birthdate ? ` (${s.birthdate})` : ''}</div>
                ))}
              </div>
            )}
            {form.group.priority === 'waitlist' && (
              <div className="mt-1 text-amber-700">
                Sie werden benachrichtigt, sobald ein weiterer passender Schüler gefunden ist.
              </div>
            )}
          </SummaryCard>
        )}
        {fc.steps.slot && !form.course_offer && (
          <SummaryCard title="Verfügbarkeit">
            <span data-testid="public-registration-summary-availability">
              {formatAvailability(form.availability) || '—'}
            </span>
          </SummaryCard>
        )}
        {form.preferred_slot && (
          <SummaryCard title="Wunschtermin">
            {formatDate(form.preferred_slot.date)}<br />
            <span className="text-slate-500">
              {formatTimeRangeUhr(form.preferred_slot.start_time, form.preferred_slot.end_time)} · bei {form.preferred_slot.teacher_name}
            </span>
          </SummaryCard>
        )}
        {fc.steps.guardian && !form.guardian.same_as_payer && form.guardian.first_name && (
          <SummaryCard title="Erziehungsberechtigte:r">
            {form.guardian.salutation ? `${form.guardian.salutation} ` : ''}{form.guardian.title ? `${form.guardian.title} ` : ''}
            {form.guardian.first_name} {form.guardian.last_name}
            {(form.guardian.email || form.guardian.phone) && (
              <div className="text-slate-500">{[form.guardian.email, form.guardian.phone].filter(Boolean).join(' · ')}</div>
            )}
          </SummaryCard>
        )}
        <SummaryCard title="Zahler">
          {form.payer.salutation ? `${form.payer.salutation} ` : ''}{form.payer.title ? `${form.payer.title} ` : ''}{form.payer.first_name} {form.payer.last_name}<br />
          <span className="text-slate-500">{form.payer.email}</span><br />
          {(form.payer.street || form.payer.house_number || form.payer.address_extra || form.payer.postal_code || form.payer.city) && (
            <span className="text-slate-500" data-testid="public-registration-summary-payer-address">
              {composePostalAddress(form.payer)}
            </span>
          )}
        </SummaryCard>
        {fc.steps.sepa && (
          <SummaryCard title="SEPA">
            {form.sepa.account_holder}<br />
            <span className="font-mono text-slate-500">{formatIban(form.sepa.iban)}</span>
          </SummaryCard>
        )}
        {answeredCustom.length > 0 && (
          <SummaryCard title="Zusätzliche Angaben">
            {answeredCustom.map((cf) => (
              <div key={cf.id}>
                <span className="text-slate-500">{cf.label}: </span>
                {cf.type === 'checkbox' ? 'Ja' : String(cf.value)}
              </div>
            ))}
          </SummaryCard>
        )}
      </div>

      <div className="border border-slate-200 rounded-lg p-4 space-y-3" data-testid="public-registration-muensterland-card">
        <h3 className="text-sm font-medium text-slate-900">Münsterland-Card / Paket Bildung und Teilhabe</h3>
        <p className="text-sm text-slate-600">
          Wir haben Anrecht auf Leistungen aus dem Bildungs- und Teilhabepaket / Münsterland-Card
          und möchten unser Guthaben auf der Münsterland-Card für den Musikunterricht an der {schoolName} verwenden.
          Mit der Eingabe der Nummer der Münsterland-Card im folgenden Textfeld geben wir unser Einverständnis,
          dass die {schoolName} die im bewilligten Zeitraum zur Verfügung stehenden Leistungen abruft.
        </p>
        <RadioGroup
          value={card.has_card ? 'ja' : 'nein'}
          onValueChange={(value) => {
            setField('muensterland_card', 'has_card', value === 'ja');
            if (value !== 'ja') setField('muensterland_card', 'number', '');
          }}
          className="space-y-1"
        >
          <label className="flex items-center gap-3 cursor-pointer text-sm text-slate-800">
            <RadioGroupItem value="ja" data-testid="public-registration-muensterland-yes" />
            Ja, eine Münsterland-Card ist vorhanden
          </label>
          <label className="flex items-center gap-3 cursor-pointer text-sm text-slate-800">
            <RadioGroupItem value="nein" data-testid="public-registration-muensterland-no" />
            Nein
          </label>
        </RadioGroup>
        {card.has_card && (
          <div>
            <label className="block text-sm text-slate-700 mb-1" htmlFor="public-registration-muensterland-number">
              Kartennummer *
            </label>
            <Input
              id="public-registration-muensterland-number"
              value={card.number}
              onChange={(e) => setField('muensterland_card', 'number', e.target.value)}
              className={invalid('muensterland_card.number') ? 'border-red-500' : ''}
              data-testid="public-registration-muensterland-number"
              autoComplete="off"
            />
          </div>
        )}
      </div>

      <div className="space-y-3 pt-2">
        <LegalBlock title="Allgemeine Geschäftsbedingungen (AGB)" text={config?.agb_text} url={config?.agb_url} testId="public-registration-agb-block" />
        <LegalBlock title="Datenschutzerklärung" text={config?.datenschutz_text} url={config?.datenschutz_url} testId="public-registration-datenschutz-block" />
        <LegalBlock title="Widerrufsbelehrung" text={config?.widerruf_text} url={config?.widerruf_url} testId="public-registration-widerruf-block" />
      </div>

      <div className="space-y-2 pt-2">
        <ConsentRow
          checked={c.agb}
          onChange={(v) => setField('consents', 'agb', !!v)}
          invalid={invalid('consents.agb')}
          testId="public-registration-consent-agb"
        >
          Ich akzeptiere die {docLink(config, 'agb')}.
        </ConsentRow>
        <ConsentRow
          checked={c.datenschutz}
          onChange={(v) => setField('consents', 'datenschutz', !!v)}
          invalid={invalid('consents.datenschutz')}
          testId="public-registration-consent-datenschutz"
        >
          Ich habe die {docLink(config, 'datenschutz')} gelesen und stimme der Verarbeitung zu.
        </ConsentRow>
        <ConsentRow
          checked={c.widerruf}
          onChange={(v) => setField('consents', 'widerruf', !!v)}
          invalid={invalid('consents.widerruf')}
          testId="public-registration-consent-widerruf"
        >
          Ich habe die {docLink(config, 'widerruf')} zur Kenntnis genommen.
        </ConsentRow>
      </div>
    </div>
  );
}
